import AppLauncherCard from '@/components/applauncher/AppLauncherCard';
import PbsLiveGroupsCard from '@/components/pbsLive/pbsLiveGroupsCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GroupMembership, HomeGroups } from '@/types/pbsLive/homeGroups';
import { Bot, Send } from 'lucide-react';
import { useContext, useEffect, useRef, useState } from 'react';
import { EventData, Joyride } from 'react-joyride';
import ClientList from '../../components/client/clientList';
import { PBSPeoplePicker } from '../../components/helper/PBSPeoplePicker';
import AnnouncementsCard from '../../components/home/AnnouncementsCard';
import JoyrideArrow from '../../components/home/JoyrideArrow';
import JoyrideTooltip from '../../components/home/JoyrideTooltip';
import LinksMenu from '../../components/home/linksMenu';
import ProviderDataSummary from '../../components/widgets/MyProviderData/ProviderDataSummary';
import IFramePanel from '../../components/IframePanel';
import { IMySettings } from '../../components/settings/IMySettings';
import BlockingSuggestionsModal from '../../components/suggestions/BlockingSuggestionsModal';
import { useApiClient, usePbsLiveClient } from '../../context/ApiContext';
import { CurrentUserContext } from '../../context/CurrentUserContext';
import { useSuggestions } from '../../context/SuggestionsContext';
import { isBlockingSuggestion } from '../../utils/suggestionsUtil';
import { getEncryptedUsername, getHomeGroups, getTreatmentGroup } from '../../services/pbsLive/PBSLive';
import { getMySiteClients } from '../../services/mysite/MySiteHome';
import { IPBSClient } from '../../types/common/IPBSClient';
import { getSteps } from '../../types/home/steps';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getDisplayFirstName } from '../../utils/userUtil';
import './home.css';

const CHAT_PHRASES = [
    'Where can I see my paystubs?',
    'I would like to fill out an incident report.',
    'Please help me start a support conversation.',
];

function useTypingPlaceholder(phrases: string[], speed = 60, pause = 1500) {
    const [placeholder, setPlaceholder] = useState('');
    const state = useRef({ phraseIndex: 0, charIndex: 0, deleting: false });
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const tick = () => {
            const { phraseIndex, deleting } = state.current;
            const current = phrases[phraseIndex];
            if (!deleting) {
                state.current.charIndex++;
                setPlaceholder(current.slice(0, state.current.charIndex));
                if (state.current.charIndex === current.length) {
                    state.current.deleting = true;
                    timer = setTimeout(tick, pause);
                    return;
                }
            } else {
                state.current.charIndex--;
                setPlaceholder(current.slice(0, state.current.charIndex));
                if (state.current.charIndex === 0) {
                    state.current.deleting = false;
                    state.current.phraseIndex = (phraseIndex + 1) % phrases.length;
                }
            }
            timer = setTimeout(tick, deleting ? speed / 2 : speed);
        };
        timer = setTimeout(tick, speed);
        return () => clearTimeout(timer);
    }, [phrases, speed, pause]);
    return placeholder;
}

interface IHomeProps {
    settings: IMySettings
}

const Home = (props: IHomeProps) => {
    useDocumentTitle('Home');
    const { currentUser, originalUser, accessControl, isOnBehalfOf, profileProperties, setCurrentUserByLoginName, isLoading } = useContext(CurrentUserContext);
    // Name/role/region come straight from the (possibly impersonated) current user —
    // the GraphQL "user" fields were the same vStaff row.
    const fullName = currentUser?.FullName ?? "";
    const role = currentUser?.Role ?? "";
    const region = currentUser?.Region ?? "";
    const [clients, setClients] = useState<IPBSClient[]>([]);
    const [homeGroups, setHomeGroups] = useState<HomeGroups | null>(null);
    const [treatmentGroups, setTreatmentGroups] = useState<GroupMembership[]>([]);
    const [encryptedUsername, setEncryptedUsername] = useState<string>("");
    const [start, setStart] = useState<boolean>(false);
    const [isMobile] = useState<boolean>(window.innerWidth < 768);
    const [blockingSuggestions, setBlockingSuggestions] = useState<any[]>([]);
    const [showBlockingModal, setShowBlockingModal] = useState<boolean>(false);
    const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
    const [previousSubmitting, setPreviousSubmitting] = useState<boolean>(false);
    const { suggestions, suggestionSubmitting, suggestionError, supportMessage, refreshSuggestions, suggestionFinishedSubmitting, isPanelOpen, panelUrl, panelSuggestionId, closePanel, dismissSuggestionById } = useSuggestions();

    const [selectedPerson, setSelectedPerson] = useState<string>("");
    const [personaError, setPersonaError] = useState<string | null>(null);
    const [chatInput, setChatInput] = useState<string>("");
    const chatPlaceholder = useTypingPlaceholder(CHAT_PHRASES);
    const pbsLiveClient = usePbsLiveClient();
    const apiClient = useApiClient();
    const [clientsLoading, setClientsLoading] = useState<boolean>(false);
    const [clientsLoaded, setClientsLoaded] = useState<boolean>(false);
    const [clientsError, setClientsError] = useState<unknown>(null);

    useEffect(() => {
        if (!isLoading && currentUser && currentUser.Id !== 0) {
            let cancelled = false;
            setSelectedPerson(currentUser.LoginName);
            setClientsLoading(true);
            setClientsError(null);
            getMySiteClients(currentUser.LoginName, apiClient)
                .then(response => {
                    if (cancelled) return;
                    const sortedClients = [...(response.data ?? [])].sort((a: IPBSClient, b: IPBSClient) => {
                        const firstName = (name: string) => name.includes(',') ? name.split(',')[1].trim() : name;
                        return firstName(a.name).localeCompare(firstName(b.name));
                    });
                    setClients(sortedClients);
                    setClientsLoaded(true);

                    const hasCompletedTutorial = localStorage.getItem('pbsTutorialCompleted');
                    if (!hasCompletedTutorial) {
                        setTimeout(() => setStart(true), 500);
                    }
                })
                .catch(error => {
                    if (cancelled) return;
                    console.error('Error fetching clients:', error);
                    setClientsError(error);
                })
                .finally(() => {
                    if (!cancelled) setClientsLoading(false);
                });
            getHomeGroups(currentUser.LoginName, pbsLiveClient)
                .then(response => {
                    setHomeGroups(response.data);
                })
                .catch(error => {
                    console.error(error);
                });
            getEncryptedUsername(currentUser.LoginName, pbsLiveClient)
                .then(response => {
                    setEncryptedUsername(response.data);
                })
                .catch(error => {
                    console.error(error);
                });
            return () => { cancelled = true; };
        }
    }, [currentUser, isLoading, apiClient, pbsLiveClient]);
    useEffect(() => {
        if (clients.length > 0) {
            let promises: Promise<any>[] = [];
            let groups: GroupMembership[] = [];
            clients.forEach(client => {
                promises.push(getTreatmentGroup(currentUser.LoginName, client.id, pbsLiveClient));
            });
            Promise.all(promises)
                .then(responses => {
                    responses.forEach(response => {
                        if (response.status === 200 && response.data) {
                            let group: GroupMembership = {
                                roomId: response.data.additionalData.clientId,
                                displayname: response.data.displayname,
                            }
                            groups.push(group);
                        }
                    });
                    setTreatmentGroups(groups);
                })
                .catch(error => {
                    console.error('Error fetching treatment groups:', error);
                });
        }
    }, [clients, pbsLiveClient, currentUser])
    useEffect(() => {
        if (suggestions && suggestions.length > 0) {
            const blockedSuggestions = suggestions.filter(suggestion => isBlockingSuggestion(suggestion));
            setBlockingSuggestions(blockedSuggestions);
            setShowBlockingModal(blockedSuggestions.length > 0);
        } else {
            setBlockingSuggestions([]);
            setShowBlockingModal(false);
        }
    }, [suggestions]);

    useEffect(() => {
        if (suggestionError) setShowErrorDialog(true);
    }, [suggestionError]);

    useEffect(() => {
        if (previousSubmitting && !suggestionSubmitting) {
            if (!suggestionError) setShowSuccessDialog(true);
        }
        setPreviousSubmitting(suggestionSubmitting);
    }, [suggestionSubmitting, suggestionError, previousSubmitting]);

    const handleErrorDialogClose = () => {
        setShowErrorDialog(false);
        suggestionFinishedSubmitting(false, null, null);
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        suggestionFinishedSubmitting(false, null, null);
    };

    const handlePanelDismiss = async () => {
        closePanel();
        if (panelSuggestionId) {
            try {
                await dismissSuggestionById(panelSuggestionId, "Action Performed");
                refreshSuggestions();
            } catch (error) {
                console.error('Error dismissing suggestion after panel action:', error);
            }
        }
    };

    const updateStateOnPersonChange = async (loginName: string) => {
        setPersonaError(null);
        setSelectedPerson(loginName);
        // The clients effect refetches once CurrentUserContext swaps currentUser.
        // A failed lookup never rejects: the context keeps the current user, so put
        // the selection back and say so instead of letting the page error out.
        const switched = await setCurrentUserByLoginName(loginName);
        if (!switched) {
            setSelectedPerson(currentUser.LoginName);
            setPersonaError(`Could not load ${loginName.replace(/^POSITIVE\\/i, '')}. Still viewing as ${currentUser.FullName || currentUser.LoginName}.`);
        }
    };

    const joyrideHandler = (data: EventData) => {
        const { status, action } = data;
        if (status === 'finished' || status === 'skipped' || action === 'skip' || action === 'close' || action === 'reset') {
            localStorage.setItem('pbsTutorialCompleted', 'true');
            setStart(false);
        }
    };
    const openCopilotChat = (message: string | null) => {
        if (window.openCopilotChat) {
            window.openCopilotChat({
                roomSubType: 'SolvPbsLivePersona',
                pendingMessage: message ? message : chatInput ? chatInput : ''
            });
        }
        setChatInput('');
    }
    const time = new Date().getHours() + 1;
    const greeting = time < 12 ? 'Good Morning' : time < 18 ? 'Good Afternoon' : 'Good Evening';
    const firstName = getDisplayFirstName(currentUser, profileProperties);
    if (clientsLoading || isLoading) {
        return (
            <div className="home-loading-container">
                <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                    <span className="text-sm text-muted-foreground">Loading...</span>
                </div>
            </div>
        );
    }

    if (clientsError) {
        console.error('Error fetching data:', clientsError);
        return (
            <Alert variant="destructive" className="m-4">
                <AlertDescription>There was an error processing your request. Please try again.</AlertDescription>
            </Alert>
        );
    }

    return (
        <>
            <Joyride
                steps={getSteps(isMobile)}
                continuous={true}
                run={start && !showBlockingModal}
                onEvent={joyrideHandler}
                tooltipComponent={JoyrideTooltip}
                arrowComponent={JoyrideArrow}
                options={{
                    showProgress: true,
                    zIndex: 10000,
                    primaryColor: 'rgb(0, 120, 215)',
                    scrollOffset: 500,
                    buttons: ['back', 'skip', 'primary'],
                    overlayClickAction: false
                }} />
            <div className="home-container">
                <div className="home-grid">
                    {/* Select Persona */}
                    {props.settings.enableOnBehalfOf && originalUser && originalUser.Id !== 0 && (
                        <div className="mt-8 md:ml-6 home-persona-section">
                            <Card>
                                <CardContent className="p-4">
                                    <PBSPeoplePicker label="Select Persona" onChange={updateStateOnPersonChange} />
                                    {personaError && (
                                        <Alert variant="destructive" className="mt-2">
                                            <AlertDescription>{personaError}</AlertDescription>
                                        </Alert>
                                    )}
                                    {fullName && (
                                        <Label className="text-foreground">
                                            <b>{fullName}</b><br />{role}, {region}
                                        </Label>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    )}
                    {/* Clients - mobile */}
                    <div className="home-clients-mobile">
                        {!clientsLoading && clientsLoaded && <div className="clients"><ClientList clients={clients} /></div>}
                    </div>

                    {/* Links Menu */}
                    <div className="m-2 md:mt-8 md:ml-6 home-links-section">
                        <Card className="mb-8">
                            <CardHeader className="pb-0">
                                <CardTitle className="text-[14pt]">PBS Copilot {currentUser ? ` - ${greeting} ${firstName}` : null}</CardTitle>
                                <CardDescription>Ask questions or give commands to interact with PBS Copilot.</CardDescription>
                            </CardHeader>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2">
                                    <div className="relative flex-1">
                                        <Bot className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" color='rgb(0, 120, 215)' />
                                        <Input
                                            className="pl-8"
                                            placeholder={chatPlaceholder}
                                            value={chatInput}
                                            onChange={(e) => setChatInput(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && openCopilotChat(null)}
                                        />
                                    </div>
                                    <Button size="icon" onClick={() => openCopilotChat(null)} aria-label="Send">
                                        <Send className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="links-menu">
                            <CardContent className="p-4">
                                {selectedPerson.length > 0 && <LinksMenu loginName={selectedPerson.replace(/^POSITIVE\\/, '')} />}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right column */}
                    <div className="home-right-column">
                        {!clientsLoading && clientsLoaded && <div className="clients home-clients-desktop"><ClientList clients={clients} /></div>}

                        <AppLauncherCard />
                        {homeGroups && <div className="groups"><PbsLiveGroupsCard groups={homeGroups} treatmentGroups={treatmentGroups} encryptedUsername={encryptedUsername} /></div>}
                        {selectedPerson.length > 0 && (
                            <AnnouncementsCard loginName={selectedPerson} hasBlockingSuggestions={blockingSuggestions.length > 0} />
                        )}
                    </div>

                    {/* Provider Data */}
                    {accessControl.canRead('widget_provider_data') && selectedPerson.length > 0 && (
                        <div className="m-2 md:mt-8 md:ml-6 home-provider-data-section pb-24">
                            <Card>
                                <CardHeader className="pb-0">
                                    <CardTitle className="text-[14pt]">Provider Data</CardTitle>
                                </CardHeader>
                                <CardContent className="p-4">
                                    <ProviderDataSummary />
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Loading Dialog */}
                    <Dialog open={suggestionSubmitting}>
                        <DialogContent className="max-w-xs" onInteractOutside={(e) => e.preventDefault()}>
                            <div className="flex flex-col items-center gap-4 py-4 text-center">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                                <span className="text-sm text-muted-foreground">Processing...</span>
                            </div>
                        </DialogContent>
                    </Dialog>

                    {/* Error Dialog */}
                    <Dialog open={showErrorDialog} onOpenChange={(open) => !open && handleErrorDialogClose()}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Error</DialogTitle>
                            </DialogHeader>
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {suggestionError || 'An error occurred while processing your request.'}
                                </AlertDescription>
                            </Alert>
                            <DialogFooter>
                                <Button variant={'secondary'} onClick={() => {openCopilotChat(supportMessage)}}>Create a support chat</Button>
                                <Button variant={'destructive'} onClick={handleErrorDialogClose}>Close</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* Success Dialog */}
                    <Dialog open={showSuccessDialog} onOpenChange={(open) => !open && handleSuccessDialogClose()}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Success</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-muted-foreground">Action completed successfully!</p>
                            <DialogFooter>
                                <Button onClick={handleSuccessDialogClose}>Close</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>

                    {/* IFrame Panel */}
                    <IFramePanel
                        isOpen={isPanelOpen}
                        onDismiss={handlePanelDismiss}
                        title="Action"
                        url={panelUrl}
                        type="large"
                        isBlocking={false}
                        focusTrapZoneProps={{}}
                    />
                </div>
            </div>
            <BlockingSuggestionsModal
                isOpen={showBlockingModal}
                blockingSuggestions={blockingSuggestions}
                refreshSuggestions={refreshSuggestions}
                dismissible={isOnBehalfOf}
                onDismiss={() => setShowBlockingModal(false)}
            />
        </>
    );
};

export default Home;
